import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, Briefcase, Building, Mail, ChevronDown, Download, 
  Plus, Edit, Trash2, X, LogOut, Save, Loader2,
  FileText, Scale, Receipt, Image, UserCheck, Coins, Palette, FileType, Layers, BarChart3, Star
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { InvoicesTab } from "./admin/InvoicesTab";
import { TeamTab } from "./admin/TeamTab";
import { DocumentStudioTab } from "./admin/DocumentStudioTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Status configurations for different entity types
const applicationStatuses = {
  new: { label: "New", color: "bg-blue-100 text-blue-700" },
  screening: { label: "Screening", color: "bg-yellow-100 text-yellow-700" },
  submitted: { label: "Submitted to Client", color: "bg-purple-100 text-purple-700" },
  interviewing: { label: "Interviewing", color: "bg-indigo-100 text-indigo-700" },
  offer: { label: "Offer Stage", color: "bg-orange-100 text-orange-700" },
  placed: { label: "Placed", color: "bg-green-100 text-green-700" },
  rejected: { label: "Not a Fit", color: "bg-red-100 text-red-700" },
  withdrawn: { label: "Withdrawn", color: "bg-gray-100 text-gray-600" }
};

const candidateStatuses = {
  new: { label: "New", color: "bg-blue-100 text-blue-700" },
  reviewed: { label: "Reviewed", color: "bg-yellow-100 text-yellow-700" },
  qualified: { label: "Qualified", color: "bg-green-100 text-green-700" },
  "in-network": { label: "In Network", color: "bg-purple-100 text-purple-700" },
  placed: { label: "Placed", color: "bg-emerald-100 text-emerald-700" },
  inactive: { label: "Inactive", color: "bg-gray-100 text-gray-600" }
};

const clientStatuses = {
  new: { label: "New Inquiry", color: "bg-blue-100 text-blue-700" },
  contacted: { label: "Contacted", color: "bg-yellow-100 text-yellow-700" },
  discovery: { label: "Discovery Call", color: "bg-purple-100 text-purple-700" },
  proposal: { label: "Proposal Sent", color: "bg-indigo-100 text-indigo-700" },
  engaged: { label: "Engaged", color: "bg-green-100 text-green-700" },
  searching: { label: "Active Search", color: "bg-orange-100 text-orange-700" },
  placed: { label: "Placement Made", color: "bg-emerald-100 text-emerald-700" },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-600" }
};

const associateStatuses = {
  applied: { label: "Applied", color: "bg-blue-100 text-blue-700" },
  screening: { label: "Screening", color: "bg-yellow-100 text-yellow-700" },
  interview: { label: "Interview", color: "bg-purple-100 text-purple-700" },
  accepted: { label: "Accepted", color: "bg-green-100 text-green-700" },
  in_residence: { label: "In Programme", color: "bg-emerald-100 text-emerald-700" },
  graduated: { label: "Graduated → Partner", color: "bg-[#B8860B]/15 text-[#B8860B]" },
  rejected: { label: "Not a Fit", color: "bg-red-100 text-red-700" }
};

const getStatusConfig = (type, status) => {
  const configs = {
    applications: applicationStatuses,
    candidates: candidateStatuses,
    "client-inquiries": clientStatuses,
    associates: associateStatuses
  };
  return configs[type]?.[status] || { label: status, color: "bg-gray-100 text-gray-600" };
};

const departments = ["Engineering", "Product", "Design", "Data", "Sales", "Growth", "Customer Success", "Marketing", "Operations"];
const seniorities = ["IC", "Mid", "Senior", "Staff", "Manager", "Director", "VP", "C-level"];
const currencies = ["USD", "GBP", "AED"];
const currencySymbols = { USD: "$", GBP: "£", AED: "AED " };
const locationTypes = ["Remote", "Hybrid", "On-site"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [savingJob, setSavingJob] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [adminList, setAdminList] = useState([]);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: "", password: "", email: "", name: "", role: "admin" });
  const [testimonials, setTestimonials] = useState([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [partners, setPartners] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [chatLeads, setChatLeads] = useState([]);
  const [plannerLeads, setPlannerLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const emptyCaseStudy = { role: "", stage: "", sector: "", days_to_hire: "", outcome: "", is_active: true, display_order: 0 };
  const [caseStudyForm, setCaseStudyForm] = useState(emptyCaseStudy);
  const [editingCaseId, setEditingCaseId] = useState(null);

  const emptyJob = {
    title: "", company: "Confidential", company_description: "",
    location: "", location_type: "Hybrid", salary_min: "",
    salary_max: "", salary_currency: "USD", job_type: "Full-time", seniority: "",
    department: "", description: "", responsibilities: [""], applicant_intro: "", requirements: [""],
    offer_intro: "", benefits: [""], highlights: [""], reference: "", is_confidential: false, is_active: true
  };

  const [jobForm, setJobForm] = useState(emptyJob);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const user = localStorage.getItem("admin_user");
    if (!token) {
      navigate("/admin");
      return;
    }
    setAdmin(JSON.parse(user));
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
  });

  const assignReferrer = async (inquiryId, uid) => {
    try {
      await axios.patch(`${API}/admin/client-inquiries/${inquiryId}/assign?uid=${encodeURIComponent(uid)}`, {}, getAuthHeaders());
      toast.success(uid ? "Referrer assigned" : "Referrer cleared");
      fetchData();
    } catch (e) {
      toast.error("Failed to update referrer");
    }
  };

  const updateAssociateStatus = async (appId, status) => {
    try {
      await axios.patch(`${API}/admin/associate-applications/${appId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success("Status updated");
      fetchData();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const saveCaseStudy = async () => {
    if (!caseStudyForm.role.trim()) { toast.error("Role is required"); return; }
    const payload = {
      ...caseStudyForm,
      days_to_hire: caseStudyForm.days_to_hire === "" || caseStudyForm.days_to_hire === null ? null : parseInt(caseStudyForm.days_to_hire),
      display_order: parseInt(caseStudyForm.display_order) || 0,
    };
    try {
      if (editingCaseId) {
        await axios.patch(`${API}/admin/case-studies/${editingCaseId}`, payload, getAuthHeaders());
        toast.success("Case study updated");
      } else {
        await axios.post(`${API}/admin/case-studies`, payload, getAuthHeaders());
        toast.success("Case study added");
      }
      setCaseStudyForm(emptyCaseStudy);
      setEditingCaseId(null);
      fetchData();
    } catch (e) {
      toast.error("Failed to save case study");
    }
  };

  const editCaseStudy = (c) => {
    setEditingCaseId(c.id);
    setCaseStudyForm({
      role: c.role || "", stage: c.stage || "", sector: c.sector || "",
      days_to_hire: c.days_to_hire ?? "", outcome: c.outcome || "",
      is_active: c.is_active !== false, display_order: c.display_order || 0,
    });
  };

  const toggleCaseStudy = async (c) => {
    try {
      await axios.patch(`${API}/admin/case-studies/${c.id}`, { is_active: !c.is_active }, getAuthHeaders());
      fetchData();
    } catch (e) { toast.error("Failed to update"); }
  };

  const deleteCaseStudy = async (id) => {
    if (!window.confirm("Delete this case study?")) return;
    try {
      await axios.delete(`${API}/admin/case-studies/${id}`, getAuthHeaders());
      toast.success("Deleted");
      if (editingCaseId === id) { setEditingCaseId(null); setCaseStudyForm(emptyCaseStudy); }
      fetchData();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, jobsRes, appsRes, candRes, inqRes, testRes, partnersRes, assocRes, caseRes, chatLeadsRes, plannerLeadsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/admin/jobs`, getAuthHeaders()),
        axios.get(`${API}/admin/applications`, getAuthHeaders()),
        axios.get(`${API}/admin/candidates`, getAuthHeaders()),
        axios.get(`${API}/admin/client-inquiries`, getAuthHeaders()),
        axios.get(`${API}/admin/testimonials`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/referral/partners`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/associate-applications`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/case-studies`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/chat-leads`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/planner-leads`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setApplications(appsRes.data);
      setCandidates(candRes.data);
      setInquiries(inqRes.data);
      setTestimonials(testRes.data);
      setPartners(partnersRes.data);
      setAssociates(assocRes.data);
      setCaseStudies(caseRes.data);
      setChatLeads(chatLeadsRes.data);
      setPlannerLeads(plannerLeadsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        toast.error("Error loading data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  const updateStatus = async (type, id, status) => {
    try {
      await axios.patch(`${API}/admin/${type}/${id}/status?status=${status}`, {}, getAuthHeaders());
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const deleteRecord = async (type, id, label = "record") => {
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, getAuthHeaders());
      toast.success("Deleted");
      setExpandedItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.status === 403 ? "Super admin access required" : "Error deleting");
    }
  };
  const isSuperAdmin = admin?.role === "super_admin";

  const openJobModal = (job = null) => {
    if (job) {
      setEditingJob(job);
      setJobForm({
        ...job,
        salary_min: job.salary_min || "",
        salary_max: job.salary_max || "",
        salary_currency: job.salary_currency || "USD",
        responsibilities: job.responsibilities?.length ? job.responsibilities : [""],
        applicant_intro: job.applicant_intro || "",
        requirements: job.requirements?.length ? job.requirements : [""],
        offer_intro: job.offer_intro || "",
        benefits: job.benefits?.length ? job.benefits : [""],
        highlights: job.highlights?.length ? job.highlights : [""],
        reference: job.reference || ""
      });
    } else {
      setEditingJob(null);
      setJobForm(emptyJob);
    }
    setShowJobModal(true);
  };

  const handleSaveJob = async (e) => {
    e.preventDefault();
    setSavingJob(true);
    
    const jobData = {
      ...jobForm,
      salary_min: jobForm.salary_min ? parseInt(jobForm.salary_min) : null,
      salary_max: jobForm.salary_max ? parseInt(jobForm.salary_max) : null,
      responsibilities: jobForm.responsibilities.filter(r => r.trim()),
      requirements: jobForm.requirements.filter(r => r.trim()),
      benefits: jobForm.benefits.filter(b => b.trim()),
      highlights: jobForm.highlights.filter(h => h.trim())
    };

    try {
      if (editingJob) {
        await axios.put(`${API}/admin/jobs/${editingJob.id}`, jobData, getAuthHeaders());
        toast.success("Job updated successfully");
      } else {
        await axios.post(`${API}/admin/jobs`, jobData, getAuthHeaders());
        toast.success("Job created successfully");
      }
      setShowJobModal(false);
      fetchData();
    } catch (error) {
      toast.error("Error saving job");
    } finally {
      setSavingJob(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, getAuthHeaders());
      toast.success("Job deleted");
      fetchData();
    } catch (error) {
      toast.error("Error deleting job");
    }
  };

  const addArrayItem = (field) => {
    setJobForm({ ...jobForm, [field]: [...jobForm[field], ""] });
  };

  const updateArrayItem = (field, index, value) => {
    const updated = [...jobForm[field]];
    updated[index] = value;
    setJobForm({ ...jobForm, [field]: updated });
  };

  const removeArrayItem = (field, index) => {
    if (jobForm[field].length === 1) return;
    const updated = jobForm[field].filter((_, i) => i !== index);
    setJobForm({ ...jobForm, [field]: updated });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatSalary = (min, max, currency = "USD") => {
    if (!min && !max) return "—";
    const sym = currencySymbols[currency] || "$";
    const fmt = (n) => `${sym}${(n / 1000).toFixed(0)}k`;
    if (min && max) return `${fmt(min)} – ${fmt(max)}`;
    return min ? `${fmt(min)}+` : `Up to ${fmt(max)}`;
  };

  // Admin management functions (super_admin only)
  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin/list`, getAuthHeaders());
      setAdminList(res.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Error loading admins");
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.username || !newAdmin.password || !newAdmin.email || !newAdmin.name) {
      toast.error("All fields are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/create`, newAdmin, getAuthHeaders());
      toast.success(`Admin '${newAdmin.username}' created`);
      setNewAdmin({ username: "", password: "", email: "", name: "", role: "admin" });
      setShowAddAdmin(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error creating admin");
    }
  };

  const handleDeleteAdmin = async (adminId, username) => {
    if (!window.confirm(`Delete admin "${username}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/${adminId}`, getAuthHeaders());
      toast.success(`Admin '${username}' deleted`);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error deleting admin");
    }
  };

  const handleToggleRole = async (adminId, currentRole) => {
    const newRole = currentRole === "super_admin" ? "admin" : "super_admin";
    if (!window.confirm(`Change role to ${newRole}?`)) return;
    try {
      await axios.put(`${API}/admin/${adminId}/role?role=${newRole}`, {}, getAuthHeaders());
      toast.success("Role updated");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error updating role");
    }
  };

  useEffect(() => {
    if (activeTab === "settings" && admin?.role === "super_admin") fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, admin?.role]);

  return (
    <div className="min-h-screen pt-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 sm:mb-12">
          <div>
            <h1 className="font-display text-2xl sm:text-4xl text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-500 mt-1 text-sm sm:text-base">
              Welcome back, {admin?.name || "Admin"}
              {admin?.role === "super_admin" && (
                <span className="ml-2 text-xs bg-[#B8860B]/10 text-[#B8860B] px-2 py-0.5 rounded-full">Super Admin</span>
              )}
              {admin?.role === "admin" && (
                <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Admin</span>
              )}
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <button onClick={fetchData} className="btn-luxury btn-luxury-outline text-xs sm:text-sm">
              Refresh
            </button>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-12">
            <div className="bg-white p-4 sm:p-6 border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Briefcase size={18} className="text-[#B8860B]" />
                <span className="text-xs sm:text-sm text-gray-500">Jobs</span>
              </div>
              <p className="text-2xl sm:text-3xl font-display">{stats.active_jobs}</p>
              <p className="text-xs text-gray-400 mt-1">{stats.total_jobs} total</p>
            </div>
            <div className="bg-white p-4 sm:p-6 border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Mail size={18} className="text-[#B8860B]" />
                <span className="text-xs sm:text-sm text-gray-500">Applications</span>
              </div>
              <p className="text-2xl sm:text-3xl font-display">{stats.new_applications}</p>
              <p className="text-xs text-gray-400 mt-1">{stats.total_applications} total</p>
            </div>
            <div className="bg-white p-4 sm:p-6 border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Users size={18} className="text-[#B8860B]" />
                <span className="text-xs sm:text-sm text-gray-500">Candidates</span>
              </div>
              <p className="text-2xl sm:text-3xl font-display">{stats.new_candidates}</p>
              <p className="text-xs text-gray-400 mt-1">{stats.total_candidates} total</p>
            </div>
            <div className="bg-white p-4 sm:p-6 border border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Building size={18} className="text-[#B8860B]" />
                <span className="text-xs sm:text-sm text-gray-500">Inquiries</span>
              </div>
              <p className="text-2xl sm:text-3xl font-display">{stats.new_client_inquiries}</p>
              <p className="text-xs text-gray-400 mt-1">{stats.total_client_inquiries} total</p>
            </div>
          </div>
        )}

        {/* Tabs - horizontally scrollable on mobile */}
        <div className="overflow-x-auto -mx-4 sm:mx-0 mb-6 sm:mb-8">
          <div className="flex gap-1 border-b border-gray-200 min-w-max px-4 sm:px-0">
            {[
              { id: "jobs", label: "Jobs", count: jobs.length },
              { id: "applications", label: "Applications", count: applications.length },
              { id: "candidates", label: "Candidates", count: candidates.length },
              { id: "inquiries", label: "Inquiries", count: inquiries.length },
              { id: "associates", label: "Associates", count: associates.length },
              { id: "testimonials", label: "Testimonials", count: testimonials.length },
              { id: "case-studies", label: "Case Studies", count: caseStudies.length },
              { id: "chat-leads", label: "Chat Leads", count: chatLeads.length },
              { id: "planner-leads", label: "Planner Leads", count: plannerLeads.length },
              { id: "invoices", label: "Invoices" },
              { id: "studio", label: "Studio" },
              { id: "team", label: "Team" },
              { id: "documents", label: "Documents" },
              ...(admin?.role === "super_admin" ? [{ id: "settings", label: "Settings" }] : [])
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 sm:px-6 py-3 text-xs sm:text-sm font-medium transition-colors relative whitespace-nowrap ${
                  activeTab === tab.id ? "text-[#B8860B]" : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                {tab.count !== undefined && <span className="ml-1 sm:ml-2 text-xs text-gray-400">({tab.count})</span>}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#B8860B]" />
                )}
              </button>
          ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-20 text-gray-400">Loading...</div>
        ) : (
          <div className="bg-white border border-gray-100">
            {/* Jobs Tab */}
            {activeTab === "jobs" && (
              <div>
                <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                  <h3 className="font-medium">Job Listings</h3>
                  <button
                    onClick={() => openJobModal()}
                    className="flex items-center gap-2 bg-[#B8860B] text-white px-4 py-2 text-sm hover:bg-[#9A7209] transition-colors"
                    data-testid="create-job-btn"
                  >
                    <Plus size={16} /> Create Job
                  </button>
                </div>
                {jobs.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">No jobs created yet</div>
                ) : (
                  jobs.map((job, i) => (
                    <div key={job.id} className={`p-6 flex items-center justify-between ${i > 0 ? "border-t border-gray-100" : ""}`}>
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-medium">{job.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded ${job.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {job.is_active ? "Active" : "Inactive"}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {job.seniority}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          {job.company} · {job.location} · {job.department} · {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openJobModal(job)}
                          className="p-2 text-gray-400 hover:text-[#B8860B] transition-colors"
                          data-testid={`edit-job-${job.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteJob(job.id)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          data-testid={`delete-job-${job.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Applications Tab */}
            {activeTab === "applications" && (
              <div>
                {applications.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">No applications yet</div>
                ) : (
                  applications.map((app, i) => (
                    <div key={app.id} className={`${i > 0 ? "border-t border-gray-100" : ""}`}>
                      <div 
                        className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedItem(expandedItem === app.id ? null : app.id)}
                      >
                        <div className="flex-grow">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-medium">{app.full_name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded ${getStatusConfig("applications", app.status).color}`}>
                              {getStatusConfig("applications", app.status).label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            Applied for: <span className="text-gray-700">{app.job_title}</span> · {formatDate(app.created_at)}
                          </p>
                        </div>
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${expandedItem === app.id ? "rotate-180" : ""}`} />
                      </div>
                      
                      {expandedItem === app.id && (
                        <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-4">
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Email</p>
                              <p className="text-sm">{app.email}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Phone</p>
                              <p className="text-sm">{app.phone || "—"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Current Role</p>
                              <p className="text-sm">{app.current_role || "—"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">LinkedIn</p>
                              {app.linkedin_url ? (
                                <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#B8860B]">View Profile</a>
                              ) : <p className="text-sm">—</p>}
                            </div>
                          </div>
                          {app.resume_filename && (
                            <div className="py-4 border-t border-gray-200">
                              <a href={`${API}/download-resume/${app.resume_filename}`} className="inline-flex items-center gap-2 text-sm text-[#B8860B]">
                                <Download size={14} /> Download Resume
                              </a>
                            </div>
                          )}
                          <div className="pt-4 border-t border-gray-200">
                            <p className="text-xs text-gray-400 mb-3">Update Pipeline Status:</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(applicationStatuses).map(([key, config]) => (
                                <button
                                  key={key}
                                  onClick={() => updateStatus("applications", app.id, key)}
                                  className={`text-xs px-3 py-1.5 border rounded transition-colors ${
                                    app.status === key 
                                      ? "bg-gray-900 text-white border-gray-900" 
                                      : "border-gray-200 hover:border-gray-400"
                                  }`}
                                >
                                  {config.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {isSuperAdmin && (
                            <div className="pt-4 mt-4 border-t border-gray-200">
                              <button
                                onClick={() => deleteRecord("applications", app.id, "application")}
                                className="px-3 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50 rounded"
                                data-testid={`delete-application-${app.id}`}
                              >
                                Delete Application
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Candidates Tab */}
            {activeTab === "candidates" && (
              <div>
                {candidates.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">No candidates registered</div>
                ) : (
                  candidates.map((cand, i) => (
                    <div key={cand.id} className={`${i > 0 ? "border-t border-gray-100" : ""}`}>
                      <div 
                        className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedItem(expandedItem === cand.id ? null : cand.id)}
                      >
                        <div className="flex-grow">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-medium">{cand.full_name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded ${getStatusConfig("candidates", cand.status).color}`}>
                              {getStatusConfig("candidates", cand.status).label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {cand.current_role} · Looking for: {cand.desired_roles?.slice(0, 2).join(", ")}
                          </p>
                        </div>
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${expandedItem === cand.id ? "rotate-180" : ""}`} />
                      </div>
                      
                      {expandedItem === cand.id && (
                        <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-4">
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Email</p>
                              <p className="text-sm">{cand.email}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Experience</p>
                              <p className="text-sm">{cand.years_experience ? `${cand.years_experience} years` : "—"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Salary Expectation</p>
                              <p className="text-sm">{cand.salary_expectation || "—"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Availability</p>
                              <p className="text-sm">{cand.availability || "—"}</p>
                            </div>
                          </div>
                          <div className="py-4 border-t border-gray-200">
                            <p className="text-xs text-gray-400 mb-2">Desired Roles</p>
                            <div className="flex flex-wrap gap-2">
                              {cand.desired_roles?.map(role => (
                                <span key={role} className="text-xs bg-white border border-gray-200 px-2 py-1">{role}</span>
                              ))}
                            </div>
                          </div>
                          <div className="pt-4 border-t border-gray-200">
                            <p className="text-xs text-gray-400 mb-3">Update Candidate Status:</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(candidateStatuses).map(([key, config]) => (
                                <button
                                  key={key}
                                  onClick={() => updateStatus("candidates", cand.id, key)}
                                  className={`text-xs px-3 py-1.5 border rounded transition-colors ${
                                    cand.status === key 
                                      ? "bg-gray-900 text-white border-gray-900" 
                                      : "border-gray-200 hover:border-gray-400"
                                  }`}
                                >
                                  {config.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {isSuperAdmin && (
                            <div className="pt-4 mt-4 border-t border-gray-200">
                              <button
                                onClick={() => deleteRecord("candidates", cand.id, "candidate")}
                                className="px-3 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50 rounded"
                                data-testid={`delete-candidate-${cand.id}`}
                              >
                                Delete Candidate
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Client Inquiries Tab */}
            {activeTab === "inquiries" && (
              <div>
                {inquiries.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">No client inquiries yet</div>
                ) : (
                  inquiries.map((inq, i) => (
                    <div key={inq.id} className={`${i > 0 ? "border-t border-gray-100" : ""}`}>
                      <div 
                        className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedItem(expandedItem === inq.id ? null : inq.id)}
                      >
                        <div className="flex-grow">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-medium">{inq.company_name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded ${getStatusConfig("client-inquiries", inq.status).color}`}>
                              {getStatusConfig("client-inquiries", inq.status).label}
                            </span>
                            {inq.referred_by_name ? (
                              <span className="text-xs px-2 py-0.5 rounded bg-[#B8860B]/10 text-[#B8860B]" data-testid="inq-referrer-badge">Ref: {inq.referred_by_name}</span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500">Unattributed</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {inq.contact_name} · Looking for: {inq.role_title}
                          </p>
                        </div>
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${expandedItem === inq.id ? "rotate-180" : ""}`} />
                      </div>
                      
                      {expandedItem === inq.id && (
                        <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-4">
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Contact</p>
                              <p className="text-sm">{inq.contact_name}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Email</p>
                              <p className="text-sm">{inq.email}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Funding Stage</p>
                              <p className="text-sm">{inq.funding_stage || "—"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Timeline</p>
                              <p className="text-sm">{inq.urgency || "—"}</p>
                            </div>
                          </div>
                          <div className="py-4 border-t border-gray-200 grid grid-cols-3 gap-6">
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Role</p>
                              <p className="text-sm font-medium">{inq.role_title}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Department</p>
                              <p className="text-sm">{inq.role_department}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Seniority</p>
                              <p className="text-sm">{inq.role_seniority}</p>
                            </div>
                          </div>
                          <div className="py-4 border-t border-gray-200">
                            <p className="text-xs text-gray-400 mb-2">Referred by (attribution)</p>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="text-sm">
                                {inq.referred_by_name ? `${inq.referred_by_name}${inq.referred_by_id ? ` (${inq.referred_by_id})` : ""}` : "Unattributed"}
                              </span>
                              <select
                                value={inq.referred_by_uid || ""}
                                onChange={(e) => assignReferrer(inq.id, e.target.value)}
                                className="text-xs border border-gray-200 rounded px-2 py-1.5"
                                data-testid="inq-assign-referrer"
                              >
                                <option value="">— Unattributed —</option>
                                {partners.map((p) => (
                                  <option key={p.uid} value={p.uid}>{p.name} ({p.member_id})</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="pt-4 border-t border-gray-200">
                            <p className="text-xs text-gray-400 mb-3">Update Client Pipeline:</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(clientStatuses).map(([key, config]) => (
                                <button
                                  key={key}
                                  onClick={() => updateStatus("client-inquiries", inq.id, key)}
                                  className={`text-xs px-3 py-1.5 border rounded transition-colors ${
                                    inq.status === key 
                                      ? "bg-gray-900 text-white border-gray-900" 
                                      : "border-gray-200 hover:border-gray-400"
                                  }`}
                                >
                                  {config.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {isSuperAdmin && (
                            <div className="pt-4 mt-4 border-t border-gray-200">
                              <button
                                onClick={() => deleteRecord("client-inquiries", inq.id, "inquiry")}
                                className="px-3 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50 rounded"
                                data-testid={`delete-inquiry-${inq.id}`}
                              >
                                Delete Inquiry
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Associate Partner Programme Tab */}
            {activeTab === "associates" && (
              <div>
                {associates.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">No programme applications yet</div>
                ) : (
                  associates.map((a, i) => (
                    <div key={a.id} className={`${i > 0 ? "border-t border-gray-100" : ""}`}>
                      <div className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50" onClick={() => setExpandedItem(expandedItem === a.id ? null : a.id)}>
                        <div className="flex-grow">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <h3 className="font-medium">{a.full_name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded ${getStatusConfig("associates", a.status).color}`}>{getStatusConfig("associates", a.status).label}</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 capitalize">{(a.track || "").replace(/_/g, " ")}</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-[#B8860B]/10 text-[#B8860B] capitalize">{a.partner_type}</span>
                            {a.referred_by_name && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500">Ref: {a.referred_by_name}</span>}
                          </div>
                          <p className="text-sm text-gray-500">{a.email}{a.location ? ` · ${a.location}` : ""}</p>
                        </div>
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${expandedItem === a.id ? "rotate-180" : ""}`} />
                      </div>
                      {expandedItem === a.id && (
                        <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-4">
                            <div><p className="text-xs text-gray-400 mb-1">Phone</p><p className="text-sm">{a.phone || "—"}</p></div>
                            <div><p className="text-xs text-gray-400 mb-1">LinkedIn</p><p className="text-sm break-all">{a.linkedin || "—"}</p></div>
                            <div><p className="text-xs text-gray-400 mb-1">Track</p><p className="text-sm capitalize">{(a.track || "").replace(/_/g, " ")}</p></div>
                            <div><p className="text-xs text-gray-400 mb-1">Partner Type</p><p className="text-sm capitalize">{a.partner_type}</p></div>
                          </div>
                          {a.experience && <div className="py-2"><p className="text-xs text-gray-400 mb-1">Experience</p><p className="text-sm">{a.experience}</p></div>}
                          {a.network && <div className="py-2"><p className="text-xs text-gray-400 mb-1">Network</p><p className="text-sm">{a.network}</p></div>}
                          {a.why && <div className="py-2"><p className="text-xs text-gray-400 mb-1">Why Magnus Kor</p><p className="text-sm">{a.why}</p></div>}
                          <div className="pt-4 border-t border-gray-200">
                            <p className="text-xs text-gray-400 mb-3">Update Application Status:</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(associateStatuses).map(([key, config]) => (
                                <button key={key} onClick={() => updateAssociateStatus(a.id, key)}
                                  className={`text-xs px-3 py-1.5 border rounded transition-colors ${a.status === key ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 hover:border-gray-400"}`}
                                  data-testid={`assoc-status-${key}`}>
                                  {config.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {isSuperAdmin && (
                            <div className="pt-4 mt-4 border-t border-gray-200">
                              <button
                                onClick={() => deleteRecord("associate-applications", a.id, "application")}
                                className="px-3 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50 rounded"
                                data-testid={`delete-associate-${a.id}`}
                              >
                                Delete Application
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}


            {/* Testimonials Tab */}
            {activeTab === "testimonials" && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-medium text-lg mb-1">Testimonials</h3>
                    <p className="text-sm text-gray-500">Manage testimonials from clients, candidates, and partners. Approved testimonials appear on the homepage.</p>
                  </div>
                  <button onClick={() => { navigator.clipboard.writeText("https://magnuskor.com/testimonial/submit"); toast.success("Shareable link copied!"); }}
                    className="px-4 py-2 text-xs bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors" data-testid="copy-testimonial-link">
                    Copy Shareable Link
                  </button>
                </div>

                {testimonials.length === 0 ? (
                  <p className="text-gray-400 text-sm py-8 text-center">No testimonials yet. Share the submission link with clients.</p>
                ) : (
                  <div className="space-y-4">
                    {testimonials.map(t => (
                      <div key={t.id} className={`border p-4 rounded ${t.status === 'approved' ? 'border-green-200 bg-green-50/30' : t.status === 'rejected' ? 'border-red-200 bg-red-50/30' : 'border-gray-200'}`} data-testid={`testimonial-${t.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-grow min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-medium text-sm text-[#1A1A1A]">{t.contact_name}</span>
                              <span className="text-xs text-gray-400">{t.role} at {t.company_name}</span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.status === 'approved' ? 'bg-green-100 text-green-700' : t.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{t.status}</span>
                              <span className="text-xs text-gray-400">{t.relationship_type}</span>
                              {t.talent_partner_name && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#B8860B]/15 text-[#8A6608] border border-[#B8860B]/30" data-testid={`credit-${t.id}`}>
                                  ✦ Credits {t.talent_partner_name} ({t.talent_partner_id})
                                </span>
                              )}
                            </div>
                            <div className="flex gap-0.5 mb-2">{[1,2,3,4,5].map(n => <Star key={n} size={12} className={n <= t.rating ? "text-[#B8860B] fill-[#B8860B]" : "text-gray-300"} />)}</div>
                            <p className="text-xs text-gray-500 mb-1 line-clamp-1"><strong>Challenge:</strong> {t.problem}</p>
                            <p className="text-xs text-gray-500 mb-1 line-clamp-1"><strong>Solution:</strong> {t.solution}</p>
                            <p className="text-xs text-gray-500 line-clamp-1"><strong>Outcome:</strong> {t.outcome}</p>
                            {t.linkedin_url && <a href={t.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#B8860B] hover:underline mt-1 inline-block">LinkedIn</a>}
                          </div>
                          <div className="flex flex-col gap-1.5 flex-shrink-0">
                            <button onClick={() => setSelectedTestimonial(t)}
                              className="px-3 py-1 text-xs border border-gray-300 text-gray-700 hover:border-[#B8860B] hover:text-[#B8860B] rounded transition-colors" data-testid={`view-full-${t.id}`}>View Full</button>
                            {t.status !== 'approved' && (
                              <button onClick={async () => { await axios.patch(`${API}/admin/testimonials/${t.id}/status?status=approved`, {}, getAuthHeaders()); fetchData(); toast.success("Approved"); }}
                                className="px-3 py-1 text-xs bg-green-600 text-white hover:bg-green-700 rounded" data-testid={`approve-${t.id}`}>Approve</button>
                            )}
                            {t.status !== 'rejected' && (
                              <button onClick={async () => { await axios.patch(`${API}/admin/testimonials/${t.id}/status?status=rejected`, {}, getAuthHeaders()); fetchData(); toast.success("Rejected"); }}
                                className="px-3 py-1 text-xs bg-yellow-600 text-white hover:bg-yellow-700 rounded" data-testid={`reject-${t.id}`}>Reject</button>
                            )}
                            <button onClick={async () => { if(window.confirm("Delete this testimonial?")) { await axios.delete(`${API}/admin/testimonials/${t.id}`, getAuthHeaders()); fetchData(); toast.success("Deleted"); }}}
                              className="px-3 py-1 text-xs bg-red-600 text-white hover:bg-red-700 rounded" data-testid={`delete-${t.id}`}>Delete</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Full-detail side panel for pre-approval review */}
                {selectedTestimonial && (
                  <div className="fixed inset-0 z-50 flex" data-testid="testimonial-detail-panel">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedTestimonial(null)} />
                    <div className="relative ml-auto w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto">
                      <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-[#1A1A1A]">Testimonial details</h4>
                          <p className="text-xs text-gray-400">Review every field before approving</p>
                        </div>
                        <button onClick={() => setSelectedTestimonial(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="close-detail-panel">×</button>
                      </div>
                      <div className="p-6 space-y-5 text-sm">
                        {/* Status & rating */}
                        <div className="flex items-center justify-between">
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${selectedTestimonial.status === 'approved' ? 'bg-green-100 text-green-700' : selectedTestimonial.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{selectedTestimonial.status}</span>
                          <div className="flex gap-0.5">{[1,2,3,4,5].map(n => <Star key={n} size={14} className={n <= selectedTestimonial.rating ? "text-[#B8860B] fill-[#B8860B]" : "text-gray-300"} />)}</div>
                        </div>

                        {/* Attribution */}
                        {selectedTestimonial.talent_partner_name && (
                          <div className="border border-[#B8860B]/30 bg-[#B8860B]/5 rounded p-3">
                            <p className="text-[10px] uppercase tracking-wider text-[#B8860B] font-medium mb-1">Credits</p>
                            <p className="text-sm text-[#1A1A1A]">{selectedTestimonial.talent_partner_name}</p>
                            <p className="text-xs text-gray-500">{selectedTestimonial.talent_partner_id}</p>
                          </div>
                        )}

                        {/* Reviewer */}
                        <div className="border-t border-gray-100 pt-4">
                          <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Reviewer</p>
                          <div className="space-y-1.5">
                            <DetailRow label="Name"          value={selectedTestimonial.contact_name} />
                            <DetailRow label="Role"          value={selectedTestimonial.role} />
                            <DetailRow label="Company"       value={selectedTestimonial.company_name} />
                            <DetailRow label="Relationship"  value={selectedTestimonial.relationship_type} />
                            {selectedTestimonial.linkedin_url && (
                              <div className="flex">
                                <span className="text-xs text-gray-400 w-24 uppercase tracking-wider">LinkedIn</span>
                                <a href={selectedTestimonial.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#B8860B] hover:underline break-all">{selectedTestimonial.linkedin_url}</a>
                              </div>
                            )}
                            {selectedTestimonial.logo_url && (
                              <div className="flex items-center">
                                <span className="text-xs text-gray-400 w-24 uppercase tracking-wider">Logo</span>
                                <img src={`${process.env.REACT_APP_BACKEND_URL}${selectedTestimonial.logo_url}`} alt="logo" className="w-16 h-16 object-contain border border-gray-100" />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Full testimonial content */}
                        <div className="border-t border-gray-100 pt-4 space-y-4">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Challenge</p>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedTestimonial.problem}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Solution</p>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedTestimonial.solution}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Outcome</p>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedTestimonial.outcome}</p>
                          </div>
                        </div>

                        {/* Timestamps */}
                        <div className="border-t border-gray-100 pt-4 text-xs text-gray-400">
                          Submitted {new Date(selectedTestimonial.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                        </div>

                        {/* Action bar */}
                        <div className="border-t border-gray-100 pt-4 flex gap-2 sticky bottom-0 bg-white pb-1">
                          {selectedTestimonial.status !== 'approved' && (
                            <button onClick={async () => { await axios.patch(`${API}/admin/testimonials/${selectedTestimonial.id}/status?status=approved`, {}, getAuthHeaders()); fetchData(); toast.success("Approved"); setSelectedTestimonial(null); }}
                              className="flex-1 px-4 py-2.5 text-sm bg-green-600 text-white hover:bg-green-700 rounded" data-testid="panel-approve">Approve & Publish</button>
                          )}
                          {selectedTestimonial.status !== 'rejected' && (
                            <button onClick={async () => { await axios.patch(`${API}/admin/testimonials/${selectedTestimonial.id}/status?status=rejected`, {}, getAuthHeaders()); fetchData(); toast.success("Rejected"); setSelectedTestimonial(null); }}
                              className="flex-1 px-4 py-2.5 text-sm bg-yellow-600 text-white hover:bg-yellow-700 rounded" data-testid="panel-reject">Reject</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Case Studies Tab */}
            {activeTab === "case-studies" && (
              <div className="p-6" data-testid="case-studies-tab">
                <div className="mb-6">
                  <h3 className="font-medium text-lg mb-1">Case Studies (Anonymised Placements)</h3>
                  <p className="text-sm text-gray-500">Add real placements with no company names. Only active entries show on the Homepage &amp; For Startups page.</p>
                </div>

                {/* Add / Edit form */}
                <div className="border border-gray-200 rounded p-5 mb-8 bg-[#FAF9F6]">
                  <p className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">
                    {editingCaseId ? "Edit case study" : "Add a case study"}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <input value={caseStudyForm.role} onChange={e => setCaseStudyForm({ ...caseStudyForm, role: e.target.value })}
                      placeholder="Role (e.g. VP of Engineering) *" className="border border-gray-300 px-3 py-2 text-sm" data-testid="cs-role" />
                    <input value={caseStudyForm.stage} onChange={e => setCaseStudyForm({ ...caseStudyForm, stage: e.target.value })}
                      placeholder="Stage (e.g. Series A)" className="border border-gray-300 px-3 py-2 text-sm" data-testid="cs-stage" />
                    <input value={caseStudyForm.sector} onChange={e => setCaseStudyForm({ ...caseStudyForm, sector: e.target.value })}
                      placeholder="Sector (e.g. Fintech)" className="border border-gray-300 px-3 py-2 text-sm" data-testid="cs-sector" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <input type="number" value={caseStudyForm.days_to_hire} onChange={e => setCaseStudyForm({ ...caseStudyForm, days_to_hire: e.target.value })}
                      placeholder="Days to hire (e.g. 21)" className="border border-gray-300 px-3 py-2 text-sm" data-testid="cs-days" />
                    <input type="number" value={caseStudyForm.display_order} onChange={e => setCaseStudyForm({ ...caseStudyForm, display_order: e.target.value })}
                      placeholder="Display order (0 = first)" className="border border-gray-300 px-3 py-2 text-sm" data-testid="cs-order" />
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={caseStudyForm.is_active} onChange={e => setCaseStudyForm({ ...caseStudyForm, is_active: e.target.checked })} data-testid="cs-active" />
                      Active (visible on site)
                    </label>
                  </div>
                  <textarea value={caseStudyForm.outcome} onChange={e => setCaseStudyForm({ ...caseStudyForm, outcome: e.target.value })}
                    placeholder="Short outcome (e.g. Placed a founding engineer who shipped the MVP in the first quarter.)" rows={2}
                    className="w-full border border-gray-300 px-3 py-2 text-sm mb-3" data-testid="cs-outcome" />
                  <div className="flex gap-2">
                    <button onClick={saveCaseStudy} className="px-5 py-2 text-sm bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors" data-testid="cs-save">
                      {editingCaseId ? "Save changes" : "Add case study"}
                    </button>
                    {editingCaseId && (
                      <button onClick={() => { setEditingCaseId(null); setCaseStudyForm(emptyCaseStudy); }}
                        className="px-5 py-2 text-sm border border-gray-300 hover:bg-gray-50" data-testid="cs-cancel">Cancel</button>
                    )}
                  </div>
                </div>

                {/* List */}
                {caseStudies.length === 0 ? (
                  <p className="text-gray-400 text-sm py-8 text-center">No case studies yet. Add your first real placement above.</p>
                ) : (
                  <div className="space-y-3">
                    {caseStudies.map(c => (
                      <div key={c.id} className={`border p-4 rounded flex items-start justify-between gap-4 ${c.is_active ? "border-green-200 bg-green-50/30" : "border-gray-200 bg-gray-50"}`} data-testid={`cs-row-${c.id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-medium text-[#1A1A1A]">{c.role}</span>
                            {c.stage && <span className="text-[10px] uppercase tracking-wider text-[#B8860B] border border-[#B8860B]/30 px-2 py-0.5">{c.stage}</span>}
                            {c.sector && <span className="text-[10px] uppercase tracking-wider text-gray-500 border border-gray-200 px-2 py-0.5">{c.sector}</span>}
                            {c.days_to_hire != null && <span className="text-xs text-gray-500">· {c.days_to_hire} days</span>}
                            {!c.is_active && <span className="text-[10px] uppercase tracking-wider text-gray-500">(hidden)</span>}
                          </div>
                          {c.outcome && <p className="text-sm text-gray-600">{c.outcome}</p>}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => toggleCaseStudy(c)} className="px-3 py-1.5 text-xs border border-gray-300 hover:bg-white" data-testid={`cs-toggle-${c.id}`}>
                            {c.is_active ? "Hide" : "Show"}
                          </button>
                          <button onClick={() => editCaseStudy(c)} className="px-3 py-1.5 text-xs border border-gray-300 hover:bg-white" data-testid={`cs-edit-${c.id}`}>Edit</button>
                          <button onClick={() => deleteCaseStudy(c.id)} className="px-3 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50" data-testid={`cs-delete-${c.id}`}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Chat Leads Tab */}
            {activeTab === "chat-leads" && (
              <div className="p-6" data-testid="chat-leads-tab">
                <div className="mb-6">
                  <h3 className="font-medium text-lg mb-1">Chatbot Leads</h3>
                  <p className="text-sm text-gray-500">People who shared their details with the website concierge. Referral credit is captured automatically.</p>
                </div>
                {chatLeads.length === 0 ? (
                  <p className="text-gray-400 text-sm py-8 text-center">No chatbot leads yet.</p>
                ) : (
                  <div className="space-y-3">
                    {chatLeads.map(l => (
                      <div key={l.id} className={`border p-4 rounded ${l.status === 'closed' ? 'border-gray-200 bg-gray-50' : 'border-green-200 bg-green-50/30'}`} data-testid={`chat-lead-${l.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-medium text-[#1A1A1A]">{l.name}</span>
                              <span className="text-[10px] uppercase tracking-wider text-[#B8860B] border border-[#B8860B]/30 px-2 py-0.5">{l.intent === 'talent' ? 'Talent' : 'Hiring'}</span>
                              <span className="text-[10px] uppercase tracking-wider text-gray-500 border border-gray-200 px-2 py-0.5">{l.status}</span>
                              {l.referred_by_name && <span className="text-[11px] text-[#B8860B]">via {l.referred_by_name}</span>}
                            </div>
                            <p className="text-sm text-gray-600">
                              <a href={`mailto:${l.email}`} className="text-[#B8860B] hover:underline">{l.email}</a>
                              {l.company ? ` · ${l.company}` : ''}
                            </p>
                            {l.need && <p className="text-xs text-gray-500 mt-1 truncate">Asked about: {l.need}</p>}
                            <p className="text-[11px] text-gray-400 mt-1">{new Date(l.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {l.transcript?.length > 0 && (
                              <button onClick={() => setSelectedLead(l)} className="px-3 py-1.5 text-xs border border-gray-300 hover:bg-white" data-testid={`chat-lead-transcript-${l.id}`}>Transcript</button>
                            )}
                            {l.status !== 'contacted' && (
                              <button onClick={async () => { await axios.patch(`${API}/admin/chat-leads/${l.id}/status?status=contacted`, {}, getAuthHeaders()); fetchData(); toast.success("Marked contacted"); }} className="px-3 py-1.5 text-xs border border-gray-300 hover:bg-white" data-testid={`chat-lead-contacted-${l.id}`}>Contacted</button>
                            )}
                            <button onClick={async () => { if(window.confirm("Delete this lead?")){ await axios.delete(`${API}/admin/chat-leads/${l.id}`, getAuthHeaders()); fetchData(); toast.success("Deleted"); }}} className="px-3 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50" data-testid={`chat-lead-delete-${l.id}`}>Delete</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {selectedLead && (
                  <div className="fixed inset-0 z-50 flex" data-testid="chat-transcript-panel">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedLead(null)} />
                    <div className="ml-auto relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
                      <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
                        <h4 className="font-medium text-[#1A1A1A]">Conversation — {selectedLead.name}</h4>
                        <button onClick={() => setSelectedLead(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
                      </div>
                      <div className="p-5 space-y-3">
                        {(selectedLead.transcript || []).map((m, i) => (
                          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#1A1A1A] text-white' : 'bg-gray-100 text-gray-700'}`}>{m.content}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Planner Leads Tab */}
            {activeTab === "planner-leads" && (
              <div className="p-6" data-testid="planner-leads-tab">
                <div className="mb-6">
                  <h3 className="font-medium text-lg mb-1">Smart Hire Planner Leads</h3>
                  <p className="text-sm text-gray-500">Founders who asked for a consultant to review their AI-generated hiring plan. Referral credit is captured automatically.</p>
                </div>
                {plannerLeads.length === 0 ? (
                  <p className="text-gray-400 text-sm py-8 text-center">No planner leads yet.</p>
                ) : (
                  <div className="space-y-3">
                    {plannerLeads.map(l => (
                      <div key={l.id} className={`border p-4 rounded ${l.status === 'contacted' ? 'border-gray-200 bg-gray-50' : 'border-green-200 bg-green-50/30'}`} data-testid={`planner-lead-${l.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-medium text-[#1A1A1A]">{l.name}</span>
                              {l.funding_stage && <span className="text-[10px] uppercase tracking-wider text-[#B8860B] border border-[#B8860B]/30 px-2 py-0.5">{l.funding_stage}</span>}
                              <span className="text-[10px] uppercase tracking-wider text-gray-500 border border-gray-200 px-2 py-0.5">{l.status}</span>
                              {l.referred_by_name && <span className="text-[11px] text-[#B8860B]">via {l.referred_by_name}</span>}
                            </div>
                            <p className="text-sm text-gray-600">
                              <a href={`mailto:${l.email}`} className="text-[#B8860B] hover:underline">{l.email}</a>
                              {l.company ? ` · ${l.company}` : ''}
                              {l.location ? ` · ${l.location}` : ''}
                            </p>
                            {l.plan_summary && <p className="text-xs text-gray-500 mt-1">{l.plan_summary}</p>}
                            {l.recommended_titles?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {l.recommended_titles.map((t, i) => (
                                  <span key={i} className="text-[11px] px-2 py-0.5 bg-white border border-gray-200 text-gray-600">{t}</span>
                                ))}
                                {l.total_annual_cost ? <span className="text-[11px] px-2 py-0.5 bg-[#B8860B]/10 border border-[#B8860B]/20 text-[#B8860B]">~{(l.currency || 'USD')} {Math.round(l.total_annual_cost).toLocaleString()}/yr</span> : null}
                              </div>
                            )}
                            <p className="text-[11px] text-gray-400 mt-2">{new Date(l.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {l.status !== 'contacted' && (
                              <button onClick={async () => { await axios.patch(`${API}/admin/planner-leads/${l.id}/status?status=contacted`, {}, getAuthHeaders()); fetchData(); toast.success("Marked contacted"); }} className="px-3 py-1.5 text-xs border border-gray-300 hover:bg-white" data-testid={`planner-lead-contacted-${l.id}`}>Contacted</button>
                            )}
                            <button onClick={async () => { if(window.confirm("Delete this lead?")){ await axios.delete(`${API}/admin/planner-leads/${l.id}`, getAuthHeaders()); fetchData(); toast.success("Deleted"); }}} className="px-3 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50" data-testid={`planner-lead-delete-${l.id}`}>Delete</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === "invoices" && <InvoicesTab />}

            {/* Document Studio Tab */}
            {activeTab === "studio" && <DocumentStudioTab />}

            {/* Team Tab */}
            {activeTab === "team" && <TeamTab />}

            {/* Documents Tab */}
            {activeTab === "documents" && (
              <div className="p-6">
                <div className="mb-8">
                  <h3 className="font-medium text-lg mb-1">Agency Documents</h3>
                  <p className="text-sm text-gray-500">Download templates, agreements, and brand assets. All documents are generated with current branding.</p>
                </div>

                {/* CLIENT DOCUMENTS */}
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">Client Documents</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { title: "Company Brochure", desc: "Full agency overview for clients & partners", icon: FileText, endpoint: "/api/documents/brochure", format: "PDF", pages: "6 pages" },
                      { title: "Client Master Services Agreement", desc: "Omnibus: fees, NDA, GDPR, engagement terms", icon: Scale, endpoint: "/api/documents/client-msa", format: "PDF", pages: "5 pages" },
                      { title: "Client Invoice", desc: "Branded invoice with candidate & CTC details", icon: Receipt, endpoint: "/api/documents/invoice-template", format: "PDF", pages: "1 page" },
                    ].map((doc, i) => (
                      <div key={i} className="group relative border border-gray-100 hover:border-[#B8860B] transition-colors rounded" data-testid={`doc-client-${i}`}>
                        <a href={`${BASE_URL}${doc.endpoint}`} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-4">
                          <doc.icon size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <div className="flex-grow min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{doc.desc}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-300">{doc.pages}</span>
                              <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> {doc.format}</span>
                            </div>
                          </div>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CONTRACTOR DOCUMENTS */}
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">Contractor Documents</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { title: "Contractor Invoice", desc: "Template for contractor commission invoicing", icon: Receipt, endpoint: "/api/documents/contractor-invoice", format: "PDF", pages: "1 page" },
                      { title: "Candidate Consent & Submission", desc: "Profile + GDPR + Right-to-Represent", icon: FileType, endpoint: "/api/documents/candidate-consent", format: "PDF", pages: "3 pages" },
                    ].map((doc, i) => (
                      <div key={i} className="group relative border border-gray-100 hover:border-[#B8860B] transition-colors rounded" data-testid={`doc-contractor-${i}`}>
                        <a href={`${BASE_URL}${doc.endpoint}`} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-4">
                          <doc.icon size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <div className="flex-grow min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{doc.desc}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-300">{doc.pages}</span>
                              <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> {doc.format}</span>
                            </div>
                          </div>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RECRUITER TOOLKIT */}
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">Recruiter Toolkit</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { title: "Recruiter Playbook", desc: "Strategy, scripts & templates — complete guide", icon: FileText, endpoint: "/api/documents/recruiter-playbook", format: "PDF", pages: "13 sections" },
                      { title: "Quick Start Card", desc: "Day-one reference card — pin to desk", icon: FileText, endpoint: "/api/documents/quick-start-card", format: "PDF", pages: "1 page" },
                      { title: "Pipeline Tracker", desc: "Client, candidate & revenue tracker", icon: FileText, endpoint: "/api/documents/pipeline-tracker", format: "XLSX", pages: "5 sheets" },
                      ...(admin?.role === "super_admin" ? [
                        { title: "Fee & Business Calculator", desc: "Client fees, revenue waterfall & business simulator", icon: FileText, endpoint: "/api/documents/fee-calculator", format: "XLSX", pages: "4 sheets", badge: "Founder" },
                      ] : []),
                      { title: "Team Earnings Calculator", desc: "Client fees, contractor & ops earnings, invoicing", icon: FileText, endpoint: "/api/documents/team-earnings-calculator", format: "XLSX", pages: "4 sheets" },
                      { title: "ATS CV Template", desc: "ATS-optimised single-column CV template", icon: FileText, endpoint: "/api/documents/ats-cv-template", format: "DOCX", pages: "Editable" },
                      { title: "Cover Letter Template", desc: "Branded candidate introduction letter", icon: FileText, endpoint: "/api/documents/cover-letter-template", format: "DOCX", pages: "Editable" },
                    ].map((doc, i) => (
                      <a key={i} href={`${BASE_URL}${doc.endpoint}`} target="_blank" rel="noopener noreferrer"
                        className={`group flex items-start gap-3 p-4 border transition-colors rounded ${doc.badge ? "border-[#B8860B]/30 bg-[#B8860B]/5 hover:border-[#B8860B]" : "border-gray-100 hover:border-[#B8860B]"}`} data-testid={`doc-toolkit-${i}`}>
                        <doc.icon size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{doc.desc}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className={`text-xs ${doc.badge ? "text-[#B8860B]" : "text-gray-300"}`}>{doc.badge || doc.pages}</span>
                            <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> {doc.format}</span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* ONBOARDING */}
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">Onboarding</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { title: "Onboarding Deck", desc: "360 recruiter onboarding — 16-slide PPTX", icon: FileText, endpoint: "/api/documents/onboarding-deck", format: "PPTX", pages: "16 slides" },
                      { title: "Diagram Assets", desc: "5 branded diagrams for onboarding deck", icon: FileText, endpoint: "/api/documents/diagram-assets", format: "PPTX", pages: "5 slides" },
                      { title: "Video Script", desc: "60-second Loom welcome video script", icon: FileText, endpoint: "/api/documents/video-script", format: "PDF", pages: "1 page" },
                    ].map((doc, i) => (
                      <a key={i} href={`${BASE_URL}${doc.endpoint}`} target="_blank" rel="noopener noreferrer"
                        className="group flex items-start gap-3 p-4 border border-gray-100 hover:border-[#B8860B] transition-colors rounded" data-testid={`doc-onboarding-${i}`}>
                        <doc.icon size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{doc.desc}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-300">{doc.pages}</span>
                            <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> {doc.format}</span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* BD TOOLKIT */}
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">BD Toolkit</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { title: "BD Playbook", desc: "10-section guide for BD contractors — commission, KPIs, scripts", icon: FileText, endpoint: "/api/documents/bd-playbook", format: "PDF", pages: "10 sections" },
                      { title: "BD Pipeline Tracker", desc: "Lead pipeline, activity log, earnings calculator, dashboard", icon: FileText, endpoint: "/api/documents/bd-pipeline-tracker", format: "XLSX", pages: "4 sheets" },
                    ].map((doc, i) => (
                      <a key={i} href={`${BASE_URL}${doc.endpoint}`} target="_blank" rel="noopener noreferrer"
                        className="group flex items-start gap-3 p-4 border border-gray-100 hover:border-[#B8860B] transition-colors rounded" data-testid={`doc-bd-${i}`}>
                        <doc.icon size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{doc.desc}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-300">{doc.pages}</span>
                            <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> {doc.format}</span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* PARTNERSHIPS — Founder Only */}
                {admin?.role === "super_admin" && (
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">Partnerships <span className="text-[10px] bg-[#B8860B] text-white px-1.5 py-0.5 rounded ml-2">Founder Only</span></h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { title: "Partnership Playbook", desc: "Strategy, outreach templates, referral fees, 90-day plan", icon: FileText, endpoint: "/api/documents/partnership-playbook", format: "PDF", pages: "10 sections" },
                      { title: "Partnership Agreement (MOU)", desc: "3-page MOU with terms, referral fees, signature blocks (signable)", icon: Scale, endpoint: "/api/documents/partnership-mou", format: "PDF", pages: "3 pages", signable: true, docType: "partnership_mou" },
                      { title: "Partner Tracker", desc: "Pipeline, referral log, earnings summary, dashboard", icon: FileText, endpoint: "/api/documents/partner-tracker", format: "XLSX", pages: "4 sheets" },
                    ].map((doc, i) => (
                      <a key={i} href={`${BASE_URL}${doc.endpoint}`} target="_blank" rel="noopener noreferrer"
                        className="group flex items-start gap-3 p-4 border border-[#B8860B]/30 bg-[#B8860B]/5 hover:border-[#B8860B] transition-colors rounded" data-testid={`doc-partnership-${i}`}>
                        <doc.icon size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{doc.desc}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-[#B8860B]">Founder Only</span>
                            <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> {doc.format}</span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
                )}

                {/* GOVERNANCE — Role-Based */}
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">Governance & Operations</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {admin?.role === "super_admin" ? (
                      <a href={`${BASE_URL}/api/documents/operational-framework`} target="_blank" rel="noopener noreferrer"
                        className="group flex items-start gap-3 p-4 border border-[#B8860B]/30 bg-[#B8860B]/5 hover:border-[#B8860B] transition-colors rounded" data-testid="doc-ops-framework">
                        <Scale size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">Operational Framework</p>
                          <p className="text-xs text-gray-400 mt-0.5">Full governance, all roles, compensation & risk clauses</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-[#B8860B]">Founder Only</span>
                            <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> PDF</span>
                          </div>
                        </div>
                      </a>
                    ) : (
                      <a href={`${BASE_URL}/api/documents/head-ops-framework`} target="_blank" rel="noopener noreferrer"
                        className="group flex items-start gap-3 p-4 border border-gray-100 hover:border-[#B8860B] transition-colors rounded" data-testid="doc-head-ops-framework">
                        <Scale size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">Operations Framework</p>
                          <p className="text-xs text-gray-400 mt-0.5">Your role, compensation, KPIs & weekly cadence</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-300">8 sections</span>
                            <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> PDF</span>
                          </div>
                        </div>
                      </a>
                    )}
                    {admin?.role === "super_admin" && (
                      <a href={`${BASE_URL}/api/documents/head-ops-framework`} target="_blank" rel="noopener noreferrer"
                        className="group flex items-start gap-3 p-4 border border-gray-100 hover:border-[#B8860B] transition-colors rounded" data-testid="doc-head-ops-framework-sa">
                        <Scale size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">Head of Ops Framework</p>
                          <p className="text-xs text-gray-400 mt-0.5">Aleksandra's version — role, comp, KPIs only</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-300">8 sections</span>
                            <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> PDF</span>
                          </div>
                        </div>
                      </a>
                    )}
                    <a href={`${BASE_URL}/api/documents/revenue-pipeline`} target="_blank" rel="noopener noreferrer"
                      className="group flex items-start gap-3 p-4 border border-gray-100 hover:border-[#B8860B] transition-colors rounded" data-testid="doc-revenue-pipeline">
                      <BarChart3 size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div className="flex-grow min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">Revenue Pipeline Playbook</p>
                        <p className="text-xs text-gray-400 mt-0.5">$100K/mo target, 5-stage pipeline, team sizing & metrics</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-300">11 sections</span>
                          <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> PDF</span>
                        </div>
                      </div>
                    </a>
                    {admin?.role === "super_admin" && (
                      <a href={`${BASE_URL}/api/documents/growth-strategy`} target="_blank" rel="noopener noreferrer"
                        className="group flex items-start gap-3 p-4 border border-[#B8860B]/30 bg-[#B8860B]/5 hover:border-[#B8860B] transition-colors rounded" data-testid="doc-growth-strategy">
                        <BarChart3 size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">Growth Strategy Playbook</p>
                          <p className="text-xs text-gray-400 mt-0.5">USA / UK / UAE: clients, candidates, roles, team splits & 30/60/90 plan</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-[#B8860B]">Founder Only</span>
                            <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> PDF</span>
                          </div>
                        </div>
                      </a>
                    )}
                    <a href={`${BASE_URL}/api/documents/associate-program`} target="_blank" rel="noopener noreferrer"
                      className="group flex items-start gap-3 p-4 border border-[#B8860B]/30 bg-[#B8860B]/5 hover:border-[#B8860B] transition-colors rounded" data-testid="doc-associate-program">
                      <BarChart3 size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div className="flex-grow min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">Associate Partner Programme</p>
                        <p className="text-xs text-gray-400 mt-0.5">Prospectus: partner ladder, tracks (BD / 360 / Ops) & compensation</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-[#B8860B]">Recruitment</span>
                          <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> PDF</span>
                        </div>
                      </div>
                    </a>
                  </div>
                </div>
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">Brand & Identity</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { title: "Brand Kit", desc: "Colours, typography, voice & tone, logo rules", icon: Palette, endpoint: "/api/documents/brand-kit", format: "PDF", pages: "6 pages" },
                      { title: "Letterhead Template", desc: "Editable Word letterhead with branding", icon: FileType, endpoint: "/api/documents/letterhead", format: "DOCX", pages: "Editable" },
                      { title: "Logo Mark (PDF)", desc: "Vector logo for print materials", icon: Image, endpoint: "/api/documents/logo", format: "PDF", pages: "1 page" },
                    ].map((doc, i) => (
                      <a key={i} href={`${BASE_URL}${doc.endpoint}`} target="_blank" rel="noopener noreferrer"
                        className="group flex items-start gap-3 p-4 border border-gray-100 hover:border-[#B8860B] transition-colors rounded" data-testid={`doc-brand-${i}`}>
                        <doc.icon size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-grow min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{doc.desc}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-300">{doc.pages}</span>
                            <span className="flex items-center gap-1 text-xs text-[#B8860B] opacity-0 group-hover:opacity-100 transition-opacity"><Download size={10} /> {doc.format}</span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Logo & Brand Assets */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Layers size={16} className="text-[#B8860B]" />
                    <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium">Logo & Brand Assets</h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[
                      { title: "Dark Background", desc: "White on charcoal", endpoint: "/api/documents/logo-dark", format: "PNG", bg: "bg-[#1A1A1A] text-white" },
                      { title: "Light Background", desc: "Charcoal on cream", endpoint: "/api/documents/logo-light", format: "PNG", bg: "bg-[#FAF9F6] text-[#1A1A1A] border border-gray-100" },
                      { title: "Transparent", desc: "No background", endpoint: "/api/documents/logo-transparent", format: "PNG", bg: "bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23eee%22%2F%3E%3Crect%20x%3D%2210%22%20y%3D%2210%22%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23eee%22%2F%3E%3C%2Fsvg%3E')] text-[#1A1A1A]" },
                      { title: "MK Monogram", desc: "Icon / avatar", endpoint: "/api/documents/monogram", format: "PNG", bg: "bg-[#1A1A1A] text-white" },
                      { title: "MK Monogram (T)", desc: "Transparent bg", endpoint: "/api/documents/monogram-transparent", format: "PNG", bg: "bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23eee%22%2F%3E%3Crect%20x%3D%2210%22%20y%3D%2210%22%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23eee%22%2F%3E%3C%2Fsvg%3E')] text-[#1A1A1A]" },
                      { title: "MK Monogram Gold", desc: "Gold on transparent", endpoint: "/api/documents/monogram-gold", format: "PNG", bg: "bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23eee%22%2F%3E%3Crect%20x%3D%2210%22%20y%3D%2210%22%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23eee%22%2F%3E%3C%2Fsvg%3E')] text-[#B8860B]" },
                      { title: "Email Signature", desc: "600x120 banner", endpoint: "/api/documents/email-signature", format: "PNG", bg: "bg-white border border-gray-100 text-[#1A1A1A]" },
                      { title: "Logo PDF", desc: "Vector for print", endpoint: "/api/documents/logo", format: "PDF", bg: "bg-[#1A1A1A] text-white" },
                    ].map((logo, i) => (
                      <a
                        key={i}
                        href={`${BASE_URL}${logo.endpoint}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-3 border border-gray-100 hover:border-[#B8860B] transition-colors rounded text-center"
                        data-testid={`admin-logo-${i}`}
                      >
                        <div className={`w-full aspect-square mb-2 flex items-center justify-center text-xs font-display rounded ${logo.bg}`}>
                          {i === 3 ? "MK" : "Magnus Kor"}
                        </div>
                        <p className="text-xs font-medium text-gray-700">{logo.title}</p>
                        <p className="text-xs text-gray-400">{logo.desc}</p>
                        <span className="flex items-center justify-center gap-1 text-xs text-[#B8860B] mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Download size={10} /> {logo.format}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

            {/* Settings Tab - Super Admin Only */}
            {activeTab === "settings" && admin?.role === "super_admin" && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <h2 className="text-lg sm:text-xl font-display">Admin Management</h2>
                  <button
                    onClick={() => setShowAddAdmin(true)}
                    className="flex items-center gap-2 bg-[#B8860B] text-white px-4 py-2 text-sm hover:bg-[#9A7209] transition-colors"
                    data-testid="add-admin-btn"
                  >
                    <Plus size={16} /> Add Admin
                  </button>
                </div>

                {/* Add Admin Form */}
                {showAddAdmin && (
                  <div className="bg-white border border-gray-200 p-4 sm:p-6 space-y-4" data-testid="add-admin-form">
                    <h3 className="font-medium text-gray-900">New Admin</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Full Name *</label>
                        <input
                          type="text"
                          value={newAdmin.name}
                          onChange={e => setNewAdmin({ ...newAdmin, name: e.target.value })}
                          className="w-full border border-gray-200 px-3 py-2 text-sm focus:border-[#B8860B] focus:outline-none"
                          placeholder="Jane Smith"
                          data-testid="new-admin-name"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Username *</label>
                        <input
                          type="text"
                          value={newAdmin.username}
                          onChange={e => setNewAdmin({ ...newAdmin, username: e.target.value })}
                          className="w-full border border-gray-200 px-3 py-2 text-sm focus:border-[#B8860B] focus:outline-none"
                          placeholder="jsmith"
                          data-testid="new-admin-username"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Email *</label>
                        <input
                          type="email"
                          value={newAdmin.email}
                          onChange={e => setNewAdmin({ ...newAdmin, email: e.target.value })}
                          className="w-full border border-gray-200 px-3 py-2 text-sm focus:border-[#B8860B] focus:outline-none"
                          placeholder="jane@magnuskor.co"
                          data-testid="new-admin-email"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Password *</label>
                        <input
                          type="password"
                          value={newAdmin.password}
                          onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })}
                          className="w-full border border-gray-200 px-3 py-2 text-sm focus:border-[#B8860B] focus:outline-none"
                          placeholder="Min 8 characters"
                          data-testid="new-admin-password"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Role</label>
                        <select
                          value={newAdmin.role}
                          onChange={e => setNewAdmin({ ...newAdmin, role: e.target.value })}
                          className="w-full border border-gray-200 px-3 py-2 text-sm focus:border-[#B8860B] focus:outline-none bg-white"
                          data-testid="new-admin-role"
                        >
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={handleCreateAdmin}
                        className="bg-[#B8860B] text-white px-4 py-2 text-sm hover:bg-[#9A7209] transition-colors"
                        data-testid="confirm-add-admin"
                      >
                        Create Admin
                      </button>
                      <button
                        onClick={() => { setShowAddAdmin(false); setNewAdmin({ username: "", password: "", email: "", name: "", role: "admin" }); }}
                        className="text-gray-500 hover:text-gray-700 px-4 py-2 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Admin List */}
                <div className="bg-white border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left">
                        <tr>
                          <th className="px-4 sm:px-6 py-3 text-xs text-gray-500 font-medium">Name</th>
                          <th className="px-4 sm:px-6 py-3 text-xs text-gray-500 font-medium hidden sm:table-cell">Username</th>
                          <th className="px-4 sm:px-6 py-3 text-xs text-gray-500 font-medium hidden md:table-cell">Email</th>
                          <th className="px-4 sm:px-6 py-3 text-xs text-gray-500 font-medium">Role</th>
                          <th className="px-4 sm:px-6 py-3 text-xs text-gray-500 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {adminList.map(a => (
                          <tr key={a.id} className="hover:bg-gray-50" data-testid={`admin-row-${a.username}`}>
                            <td className="px-4 sm:px-6 py-4">
                              <p className="font-medium text-gray-900">{a.name}</p>
                              <p className="text-xs text-gray-400 sm:hidden">{a.username}</p>
                            </td>
                            <td className="px-4 sm:px-6 py-4 text-gray-600 hidden sm:table-cell">{a.username}</td>
                            <td className="px-4 sm:px-6 py-4 text-gray-600 hidden md:table-cell">{a.email}</td>
                            <td className="px-4 sm:px-6 py-4">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                a.role === "super_admin"
                                  ? "bg-[#B8860B]/10 text-[#B8860B]"
                                  : "bg-gray-100 text-gray-600"
                              }`}>
                                {a.role === "super_admin" ? "Super Admin" : "Admin"}
                              </span>
                            </td>
                            <td className="px-4 sm:px-6 py-4">
                              {a.id !== admin?.id ? (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleToggleRole(a.id, a.role)}
                                    className="text-xs text-[#B8860B] hover:underline"
                                    data-testid={`toggle-role-${a.username}`}
                                  >
                                    {a.role === "super_admin" ? "Demote" : "Promote"}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteAdmin(a.id, a.username)}
                                    className="text-xs text-red-500 hover:underline"
                                    data-testid={`delete-admin-${a.username}`}
                                  >
                                    Delete
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">You</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {adminList.length === 0 && (
                    <div className="text-center py-8 text-gray-400 text-sm">Loading admins...</div>
                  )}
                </div>
              </div>
            )}
          </div>

      {/* Job Modal */}
      {showJobModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="font-display text-xl">{editingJob ? "Edit Job" : "Create Job"}</h2>
              <button onClick={() => setShowJobModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveJob} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">Job Title *</label>
                  <input
                    type="text"
                    required
                    value={jobForm.title}
                    onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none"
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-2">Company</label>
                  <input
                    type="text"
                    value={jobForm.company}
                    onChange={(e) => setJobForm({ ...jobForm, company: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-2">Location *</label>
                  <input
                    type="text"
                    required
                    value={jobForm.location}
                    onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none"
                    placeholder="e.g., San Francisco, CA"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-2">Department *</label>
                  <select
                    required
                    value={jobForm.department}
                    onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none bg-white"
                  >
                    <option value="">Select department</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-2">Seniority *</label>
                  <select
                    required
                    value={jobForm.seniority}
                    onChange={(e) => setJobForm({ ...jobForm, seniority: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none bg-white"
                  >
                    <option value="">Select seniority</option>
                    {seniorities.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-2">Location Type</label>
                  <select
                    value={jobForm.location_type}
                    onChange={(e) => setJobForm({ ...jobForm, location_type: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none bg-white"
                  >
                    {locationTypes.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-2">Currency</label>
                  <select
                    value={jobForm.salary_currency}
                    onChange={(e) => setJobForm({ ...jobForm, salary_currency: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none bg-white"
                    data-testid="job-currency"
                  >
                    {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-2">Salary Min</label>
                  <input
                    type="number"
                    value={jobForm.salary_min}
                    onChange={(e) => setJobForm({ ...jobForm, salary_min: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none"
                    placeholder="e.g., 150000"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-2">Salary Max</label>
                  <input
                    type="number"
                    value={jobForm.salary_max}
                    onChange={(e) => setJobForm({ ...jobForm, salary_max: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none"
                    placeholder="e.g., 200000"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">About Our Client</label>
                  <textarea
                    rows={3}
                    value={jobForm.company_description}
                    onChange={(e) => setJobForm({ ...jobForm, company_description: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none resize-none"
                    placeholder="Our client is a Series B fintech-infrastructure company backed by tier-1 VCs..."
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">Job Description (intro) *</label>
                  <textarea
                    required
                    rows={3}
                    value={jobForm.description}
                    onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none resize-none"
                    placeholder="Our client is hiring a... (short intro line)"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">Responsibilities (Job Description bullets)</label>
                  {jobForm.responsibilities.map((r, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input type="text" value={r} onChange={(e) => updateArrayItem("responsibilities", i, e.target.value)}
                        className="flex-grow border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none" placeholder="e.g., Design and build core backend services" />
                      <button type="button" onClick={() => removeArrayItem("responsibilities", i)} className="text-gray-400 hover:text-red-600 px-2"><X size={16} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addArrayItem("responsibilities")} className="text-sm text-[#B8860B]">+ Add responsibility</button>
                </div>

                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">The Successful Applicant — intro line</label>
                  <textarea
                    value={jobForm.applicant_intro}
                    onChange={(e) => setJobForm({ ...jobForm, applicant_intro: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none resize-none"
                    placeholder="We're looking for... (short intro line above the requirements)"
                    data-testid="job-applicant-intro-input"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">The Successful Applicant (requirements)</label>
                  {jobForm.requirements.map((req, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={req}
                        onChange={(e) => updateArrayItem("requirements", i, e.target.value)}
                        className="flex-grow border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none"
                        placeholder="e.g., 5+ years experience"
                      />
                      <button type="button" onClick={() => removeArrayItem("requirements", i)} className="text-gray-400 hover:text-red-600 px-2">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addArrayItem("requirements")} className="text-sm text-[#B8860B]">
                    + Add requirement
                  </button>
                </div>

                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">What's on Offer — intro line</label>
                  <textarea
                    value={jobForm.offer_intro}
                    onChange={(e) => setJobForm({ ...jobForm, offer_intro: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none resize-none"
                    placeholder="This is a high-impact role... (short intro line above the benefits)"
                    data-testid="job-offer-intro-input"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">What's on Offer (benefits)</label>
                  {jobForm.benefits.map((ben, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={ben}
                        onChange={(e) => updateArrayItem("benefits", i, e.target.value)}
                        className="flex-grow border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none"
                        placeholder="e.g., Competitive equity"
                      />
                      <button type="button" onClick={() => removeArrayItem("benefits", i)} className="text-gray-400 hover:text-red-600 px-2">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addArrayItem("benefits")} className="text-sm text-[#B8860B]">
                    + Add benefit
                  </button>
                </div>

                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">Highlights (2 hook bullets on the card)</label>
                  {jobForm.highlights.map((h, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input type="text" value={h} onChange={(e) => updateArrayItem("highlights", i, e.target.value)}
                        className="flex-grow border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none" placeholder="e.g., Fully remote (US) with meaningful equity" />
                      <button type="button" onClick={() => removeArrayItem("highlights", i)} className="text-gray-400 hover:text-red-600 px-2"><X size={16} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addArrayItem("highlights")} className="text-sm text-[#B8860B]">+ Add highlight</button>
                </div>

                <div className="col-span-2">
                  <label className="text-sm text-gray-600 block mb-2">Reference number</label>
                  <input type="text" value={jobForm.reference} onChange={(e) => setJobForm({ ...jobForm, reference: e.target.value })}
                    className="w-full border border-gray-200 px-4 py-2 focus:border-[#B8860B] focus:outline-none" placeholder="e.g., MK-2608-0001" data-testid="job-reference-input" />
                </div>

                <div className="col-span-2 flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={jobForm.is_confidential}
                      onChange={(e) => setJobForm({ ...jobForm, is_confidential: e.target.checked })}
                      className="w-4 h-4 text-[#B8860B]"
                    />
                    <span className="text-sm text-gray-600">Confidential listing</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={jobForm.is_active}
                      onChange={(e) => setJobForm({ ...jobForm, is_active: e.target.checked })}
                      className="w-4 h-4 text-[#B8860B]"
                    />
                    <span className="text-sm text-gray-600">Active (visible on job board)</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowJobModal(false)}
                  className="px-6 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingJob}
                  className="flex items-center gap-2 bg-[#B8860B] text-white px-6 py-2 hover:bg-[#9A7209] transition-colors disabled:opacity-50"
                >
                  {savingJob ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {editingJob ? "Update Job" : "Create Job"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

function DetailRow({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex">
      <span className="text-xs text-gray-400 w-24 uppercase tracking-wider shrink-0">{label}</span>
      <span className={`text-xs text-gray-700 break-words ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
